import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  ArrowLeft,
  ArrowRight,
  Building,
  Eye,
  EyeOff,
  Layers,
  Loader2,
  UserRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Stepper, useStepper } from '@/components/ui/stepper';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { formatDate } from '@/lib/utils/date';
import { useAuthStore } from '@/lib/auth/store';
import { useRegisterUser } from '../api';
import { useGroups, useLocations, useTtsPlans, useUserModules, useVoicePlans } from '../lookups/api';
import { toLegacyExpiry } from '../format';
import { createSubUserSchema, type CreateSubUserInput } from '../schema';

const STEP_FIELDS: Record<number, Array<keyof CreateSubUserInput>> = {
  0: ['username', 'password', 'confirmPassword', 'name', 'emailid', 'number'],
  1: ['company', 'address', 'pincode'],
  2: ['accountType', 'expiryDate', 'planId', 'moduleIds', 'ttsPlanId', 'ttsCredits', 'groupIds', 'locationIds'],
  3: [],
};

const STEPS = [
  { id: 'account', label: 'Account', description: 'Login & contact', icon: UserRound },
  { id: 'org', label: 'Organization', description: 'Company & address', icon: Building },
  { id: 'access', label: 'Access & Resources', description: 'Plan, modules, allocations', icon: Layers },
  { id: 'review', label: 'Review', description: 'Confirm details' },
] as const;

/** Module IDs that turn on TTS-related fields. Matches legacy CreateSubUser. */
const TTS_MODULE_IDS = new Set(['7', '10']);

export function CreateSubUserPage() {
  const navigate = useNavigate();
  const parentId = Number(useAuthStore((s) => s.userId));
  const [showPassword, setShowPassword] = useState(false);

  const voicePlans = useVoicePlans(parentId);
  const ttsPlans = useTtsPlans(parentId);
  // Platform-wide module list.
  // A USER creating a sub-user can only grant modules they themselves hold.
  const modules = useUserModules(parentId);
  const groups = useGroups();
  const locations = useLocations();

  const register = useRegisterUser();
  const stepper = useStepper(STEPS.length);

  const form = useForm<CreateSubUserInput>({
    resolver: zodResolver(createSubUserSchema),
    mode: 'onTouched',
    defaultValues: {
      username: '',
      password: '',
      confirmPassword: '',
      name: '',
      emailid: '',
      number: '',
      company: '',
      address: '',
      pincode: '',
      accountType: '0',
      expiryDate: '',
      planId: '',
      ttsPlanId: '',
      moduleIds: [],
      ttsCredits: '',
      groupIds: [],
      locationIds: [],
    },
  });

  const selectedModuleIds = form.watch('moduleIds');
  const ttsEnabled = useMemo(
    () => selectedModuleIds.some((id) => TTS_MODULE_IDS.has(id)),
    [selectedModuleIds],
  );

  const moduleOptions = useMemo(
    () =>
      (modules.data ?? []).map((m) => ({
        value: String(m.moduleId),
        label: m.moduleName,
      })),
    [modules.data],
  );
  const groupOptions = useMemo(
    () => (groups.data ?? []).map((g) => ({ value: String(g.groupId), label: g.groupName })),
    [groups.data],
  );
  const locationOptions = useMemo(
    () =>
      (locations.data ?? []).map((l) => ({
        value: String(l.locationId),
        label: l.locationName,
      })),
    [locations.data],
  );

  const handleNext = async () => {
    const fields = STEP_FIELDS[stepper.current];
    if (!fields || fields.length === 0) {
      stepper.next();
      return;
    }
    const ok = await form.trigger(fields);
    if (ok) stepper.next();
  };

  const onSubmit = (input: CreateSubUserInput) => {
    register.mutate(
      {
        username: input.username.trim(),
        password: input.password,
        name: input.name.trim(),
        emailid: input.emailid.trim(),
        number: input.number.trim(),
        company: input.company.trim(),
        address: input.address?.trim() ?? '',
        pincode: input.pincode?.trim() ?? '',
        parent: parentId,
        accountType: Number(input.accountType) as 0 | 1,
        userType: 'suser',
        expiryDate: toLegacyExpiry(input.expiryDate),
        // Legacy UserInfo defaults — see CreateUserPage comment.
        sub: 0,
        userId2: 0,
        aclId: 0,
        settings: {},
        planId: Number(input.planId),
        ttsPlanId:
          ttsEnabled && input.ttsPlanId ? Number(input.ttsPlanId) : undefined,
        ttsCredits: ttsEnabled ? (input.ttsCredits || '0') : '0',
        moduleId: input.moduleIds.length > 0 ? input.moduleIds.join(',') : null,
        // Legacy wire: groupRows/locationRows are JSON-stringified OBJECTS
        // wrapping the array — `{"groupsList":[…]}` / `{"locationsList":[…]}`.
        // Backend does `new JSONObject(...)` and rejects bare arrays.
        groupRows:
          input.groupIds.length > 0
            ? JSON.stringify({
                groupsList: input.groupIds.map((id) => {
                  const g = groupOptions.find((o) => o.value === id);
                  return { groupId: Number(id), groupName: g?.label ?? '' };
                }),
              })
            : undefined,
        locationRows:
          input.locationIds.length > 0
            ? JSON.stringify({
                locationsList: input.locationIds.map((id) => {
                  const l = locationOptions.find((o) => o.value === id);
                  return { locationId: Number(id), locationName: l?.label ?? '' };
                }),
              })
            : undefined,
      },
      {
        onSuccess: (res) => {
          if (res?.message && res.message.toLowerCase().includes('exist')) {
            form.setError('username', { message: res.message });
            stepper.goTo(0);
            return;
          }
          toast.success(res?.message ?? 'Sub user created');
          navigate('/sub-user-list');
        },
        onError: (err) => {
          toast.error(readErrorMessage(err) ?? 'Could not create the sub user.');
        },
      },
    );
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Create Sub User"
        description="Create a sub user under your account. Plans and modules are inherited from your own assignments."
        actions={
          <Button asChild variant="outline">
            <Link to="/sub-user-list">
              <ArrowLeft className="h-4 w-4" />
              Back to list
            </Link>
          </Button>
        }
      />

      <Card>
        <CardContent className="p-6">
          <Stepper
            steps={[...STEPS]}
            current={stepper.current}
            completed={stepper.completed}
            onStepClick={(i) => i <= stepper.current && stepper.goTo(i)}
          />
        </CardContent>
      </Card>

      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          {stepper.current === 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Account</CardTitle>
                <CardDescription>Login credentials and direct contact details.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Username<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} autoComplete="off" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Full name<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} autoComplete="name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Password<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            className="pr-9"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Confirm password<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="emailid"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Email<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} type="email" autoComplete="email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Phone<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(e.target.value.replace(/\D/g, '').slice(0, 10))
                          }
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                          type="tel"
                          inputMode="numeric"
                          maxLength={10}
                          autoComplete="tel"
                          placeholder="10-digit mobile number"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          {stepper.current === 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Organization</CardTitle>
                <CardDescription>Company and mailing address.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Company</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>
                        Address<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="pincode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Pincode<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} inputMode="numeric" maxLength={10} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          {stepper.current === 2 && (
            <AccessAndResourcesStep
              form={form}
              voicePlans={voicePlans.data ?? []}
              ttsPlans={ttsPlans.data ?? []}
              moduleOptions={moduleOptions}
              groupOptions={groupOptions}
              locationOptions={locationOptions}
              ttsEnabled={ttsEnabled}
              isLoadingPlans={voicePlans.isLoading}
              isLoadingModules={modules.isLoading}
              isLoadingTtsPlans={ttsPlans.isLoading}
            />
          )}

          {stepper.current === 3 && (
            <ReviewStep
              form={form}
              voicePlans={voicePlans.data ?? []}
              ttsPlans={ttsPlans.data ?? []}
              moduleOptions={moduleOptions}
              groupOptions={groupOptions}
              locationOptions={locationOptions}
              ttsEnabled={ttsEnabled}
            />
          )}

          <div className="sticky bottom-0 -mx-4 mt-2 flex items-center justify-between gap-3 border-t bg-background/95 px-4 py-3 backdrop-blur lg:-mx-8 lg:px-8">
            <Button
              type="button"
              variant="ghost"
              onClick={stepper.prev}
              disabled={stepper.isFirst || register.isPending}
            >
              <ArrowLeft className="h-4 w-4" />
              Previous
            </Button>
            <div className="text-xs text-muted-foreground">
              Step {stepper.current + 1} of {STEPS.length}
            </div>
            <div className="flex items-center gap-2">
              <Button asChild variant="ghost">
                <Link to="/sub-user-list">Cancel</Link>
              </Button>
              {!stepper.isLast ? (
                <Button type="button" onClick={handleNext}>
                  Next
                  <ArrowRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button type="submit" disabled={register.isPending}>
                  {register.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Create sub user
                </Button>
              )}
            </div>
          </div>
        </form>
      </Form>
    </ScrollPage>
  );
}

interface AccessStepProps {
  form: ReturnType<typeof useForm<CreateSubUserInput>>;
  voicePlans: Array<{ planId: number; planName: string }>;
  ttsPlans: Array<{ planId: number; planName: string }>;
  moduleOptions: Array<{ value: string; label: string }>;
  groupOptions: Array<{ value: string; label: string }>;
  locationOptions: Array<{ value: string; label: string }>;
  ttsEnabled: boolean;
  isLoadingPlans: boolean;
  isLoadingTtsPlans: boolean;
  isLoadingModules: boolean;
}

function AccessAndResourcesStep({
  form,
  voicePlans,
  ttsPlans,
  moduleOptions,
  groupOptions,
  locationOptions,
  ttsEnabled,
  isLoadingPlans,
  isLoadingTtsPlans,
  isLoadingModules,
}: AccessStepProps) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Plan &amp; access</CardTitle>
          <CardDescription>Billing mode, validity, and the voice plan that prices calls.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <FormField
            control={form.control}
            name="accountType"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>
                  Account type<span className="ml-0.5 text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="grid grid-cols-2 gap-3"
                  >
                    {(
                      [
                        { value: '0', label: 'Promotional' },
                        { value: '1', label: 'Transactional' },
                      ] as const
                    ).map((opt) => (
                      <label
                        key={opt.value}
                        className="flex cursor-pointer items-center gap-3 rounded-md border bg-card p-3 hover:border-ring"
                      >
                        <RadioGroupItem value={opt.value} />
                        <span className="text-sm font-medium">{opt.label}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="expiryDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Validity expires on<span className="ml-0.5 text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <DatePicker
                    value={field.value ? new Date(field.value) : null}
                    onChange={(d) => field.onChange(d ? d.toISOString() : '')}
                    fromDate={new Date()}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="planId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Voice plan<span className="ml-0.5 text-destructive">*</span>
                </FormLabel>
                <Select value={field.value} onValueChange={field.onChange} disabled={isLoadingPlans}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={isLoadingPlans ? 'Loading…' : 'Pick a voice plan'} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {voicePlans.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground">
                        No voice plans available to assign.
                      </div>
                    ) : (
                      voicePlans.map((p) => (
                        <SelectItem key={p.planId} value={String(p.planId)}>
                          {p.planName}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Modules</CardTitle>
          <CardDescription>
            Capabilities this sub user can access. Selecting a TTS module enables the TTS plan and
            credit fields below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={form.control}
            name="moduleIds"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Assigned modules</FormLabel>
                <FormControl>
                  <MultiSelect
                    options={moduleOptions}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={isLoadingModules ? 'Loading modules…' : 'Pick modules'}
                    searchPlaceholder="Filter modules…"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {ttsEnabled && (
            <div className="grid gap-4 rounded-md border bg-muted/30 p-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="ttsPlanId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>TTS plan</FormLabel>
                    <Select
                      value={field.value ?? ''}
                      onValueChange={field.onChange}
                      disabled={isLoadingTtsPlans}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={isLoadingTtsPlans ? 'Loading…' : 'Pick a TTS plan'}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ttsPlans.length === 0 ? (
                          <div className="px-3 py-2 text-xs text-muted-foreground">
                            No TTS plans available.
                          </div>
                        ) : (
                          ttsPlans.map((p) => (
                            <SelectItem key={p.planId} value={String(p.planId)}>
                              {p.planName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="ttsCredits"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>TTS credits (₹)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        placeholder="0"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Allocations</CardTitle>
          <CardDescription>
            Optional. Restrict this sub user to specific groups and locations they can target.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <FormField
            control={form.control}
            name="groupIds"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Groups</FormLabel>
                <FormControl>
                  <MultiSelect
                    options={groupOptions}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Any group"
                    searchPlaceholder="Filter groups…"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="locationIds"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Locations</FormLabel>
                <FormControl>
                  <MultiSelect
                    options={locationOptions}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Any location"
                    searchPlaceholder="Filter locations…"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}

interface ReviewStepProps {
  form: ReturnType<typeof useForm<CreateSubUserInput>>;
  voicePlans: Array<{ planId: number; planName: string }>;
  ttsPlans: Array<{ planId: number; planName: string }>;
  moduleOptions: Array<{ value: string; label: string }>;
  groupOptions: Array<{ value: string; label: string }>;
  locationOptions: Array<{ value: string; label: string }>;
  ttsEnabled: boolean;
}

function ReviewStep({
  form,
  voicePlans,
  ttsPlans,
  moduleOptions,
  groupOptions,
  locationOptions,
  ttsEnabled,
}: ReviewStepProps) {
  const v = form.getValues();
  const expiry = v.expiryDate ? formatDate(new Date(v.expiryDate)) : '—';
  const planName = voicePlans.find((p) => String(p.planId) === v.planId)?.planName ?? '—';
  const ttsPlanName = ttsPlans.find((p) => String(p.planId) === v.ttsPlanId)?.planName ?? '—';
  const moduleLabels = v.moduleIds
    .map((id) => moduleOptions.find((o) => o.value === id)?.label ?? id)
    .join(', ');
  const groupLabels = v.groupIds
    .map((id) => groupOptions.find((o) => o.value === id)?.label ?? id)
    .join(', ');
  const locationLabels = v.locationIds
    .map((id) => locationOptions.find((o) => o.value === id)?.label ?? id)
    .join(', ');

  return (
    <Card>
      <CardHeader>
        <CardTitle>Review</CardTitle>
        <CardDescription>
          Confirm the new sub user before creating. Click any earlier step header to edit.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <ReviewSection title="Account">
          <Row label="Username" value={v.username} />
          <Row label="Full name" value={v.name} />
          <Row label="Email" value={v.emailid} />
          <Row label="Phone" value={v.number} />
        </ReviewSection>
        <ReviewSection title="Organization">
          <Row label="Company" value={v.company} />
          <Row label="Address" value={v.address ?? ''} multiline />
          <Row label="Pincode" value={v.pincode ?? ''} />
        </ReviewSection>
        <ReviewSection title="Access">
          <Row
            label="Account type"
            value={v.accountType === '1' ? 'Transactional' : 'Promotional'}
          />
          <Row label="Validity expires" value={expiry} />
          <Row label="Voice plan" value={planName} />
          <Row label="Modules" value={moduleLabels || '—'} multiline />
          {ttsEnabled && (
            <>
              <Row label="TTS plan" value={ttsPlanName} />
              <Row label="TTS credits" value={`₹${v.ttsCredits || '0'}`} />
            </>
          )}
        </ReviewSection>
        <ReviewSection title="Allocations">
          <Row label="Groups" value={groupLabels || 'Any'} multiline />
          <Row label="Locations" value={locationLabels || 'Any'} multiline />
        </ReviewSection>
      </CardContent>
    </Card>
  );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2">{children}</dl>
    </div>
  );
}

function Row({ label, value, multiline }: { label: string; value?: string; multiline?: boolean }) {
  return (
    <div className={multiline ? 'md:col-span-2' : undefined}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`text-sm font-medium text-foreground ${multiline ? 'whitespace-pre-line' : ''}`}>
        {value || '—'}
      </dd>
    </div>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (!err) return undefined;
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
